import { useEffect, useMemo, useRef } from 'react';
import { Progress } from 'src/components/progress/Progress';
import { IGcfProductListProductImage } from 'src/interfaces';
import { lang } from 'src/utils';
import { getPreviewUrlSrc } from 'src/utils/gcf-helper';

import { ProductAttributeAccordion } from './modules/productAttributeAccordion';
import {
  applyImageVisibility,
  applyVisibilityToFlavor,
  getDefaultFlavorForSize,
  getNewlyCheckedFlavor,
  getPreviewAssetNames,
  IAttributeSize,
} from './ProductOverrideRightSideBar.helper';
import { IProductOverrideRightSideBar } from './ProductOverrideRightSideBar.interface';
import {
  EmptyStateMessage,
  EmptyStateWrapper,
  InitialLoadingWrapper,
  ProductOverrideRightSideBarContentWrapper,
  ProductOverrideRightSideBarHeader,
  ProductOverrideRightSideBarWrapper,
} from './ProductOverrideRightSideBar.styled';

export const ProductOverrideRightSideBar = (
  props: IProductOverrideRightSideBar,
) => {
  const {
    localAttributes,
    defaultSize,
    defaultFlavor,
    showImage,
    defaultImage,
    onAttributeChange,
    isLoading,
    productVariationsAssets,
  } = props;

  const preloadedPreviewImagesRef = useRef<Record<string, HTMLImageElement>>(
    {},
  );

  const previewAssetNamesToPreload = useMemo(
    () => getPreviewAssetNames(localAttributes, productVariationsAssets),
    [localAttributes, productVariationsAssets],
  );

  useEffect(() => {
    previewAssetNamesToPreload.forEach((assetName) => {
      if (preloadedPreviewImagesRef.current[assetName]) {
        return;
      }

      const image = new Image();
      image.src = getPreviewUrlSrc(assetName);
      preloadedPreviewImagesRef.current[assetName] = image;
    });
  }, [previewAssetNamesToPreload]);

  const handleChange = (updated: IAttributeSize[]) => {
    const newlyCheckedFlavor = getNewlyCheckedFlavor(localAttributes, updated);

    if (showImage && newlyCheckedFlavor) {
      onAttributeChange({
        attributeSizes: applyVisibilityToFlavor(
          updated,
          newlyCheckedFlavor.sizeName,
          newlyCheckedFlavor.flavorProductCode,
        ),
        defaultSize: newlyCheckedFlavor.sizeName,
        showImage: true,
      });

      return;
    }

    onAttributeChange({
      attributeSizes: showImage
        ? applyImageVisibility(
            updated,
            defaultSize,
            getDefaultFlavorForSize(updated, defaultFlavor, defaultSize),
          )
        : updated,
    });
  };

  const handleDefaultSizeChange = (
    sizeName?: string,
    overrideAttributes?: IAttributeSize[],
  ) => {
    const source = overrideAttributes ?? localAttributes;
    const reImaged = applyImageVisibility(
      source,
      sizeName,
      getDefaultFlavorForSize(source, defaultFlavor, sizeName),
    );
    const newDefaultSize = reImaged.find((s) => s.name === sizeName);
    const nextShowImage = Boolean(
      newDefaultSize?.flavors.some((f) => f.images?.some((img) => img.visible)),
    );

    onAttributeChange({
      attributeSizes: reImaged,
      defaultSize: sizeName,
      showImage: nextShowImage,
    });
  };

  const handleDefaultFlavorChange = (
    sizeName: string,
    flavorName: string,
    overrideAttributes?: IAttributeSize[],
  ) => {
    const source = overrideAttributes ?? localAttributes;
    const reImaged = applyVisibilityToFlavor(source, sizeName, flavorName);

    onAttributeChange({
      attributeSizes: reImaged,
      defaultSize: sizeName,
      showImage: true,
    });
  };

  const handleShowImageChange = (show: boolean) => {
    if (!show) {
      const cleared = localAttributes.map((s) => ({
        ...s,
        flavors: s.flavors.map((f) => ({
          ...f,
          images: (f.images ?? []).map((img) => ({ ...img, visible: false })),
        })),
      }));
      onAttributeChange({
        attributeSizes: cleared,
        showImage: false,
        defaultSize,
      });
    } else {
      const targetSize = defaultSize ?? localAttributes[0]?.name;
      const reImaged = applyImageVisibility(
        localAttributes,
        targetSize,
        defaultFlavor,
      );
      onAttributeChange({
        attributeSizes: reImaged,
        showImage: true,
        defaultSize: targetSize,
      });
    }
  };

  const handleProductImageChange = (
    updated: IAttributeSize[],
    updatedFlavorAssetMap: Record<string, IGcfProductListProductImage[]>,
  ) => {
    onAttributeChange({
      attributeSizes: applyImageVisibility(updated, defaultSize, defaultFlavor),
      showImage: true,
      defaultSize,
      updatedFlavorAssetMap,
    });
  };

  return (
    <ProductOverrideRightSideBarWrapper>
      <ProductOverrideRightSideBarHeader variant="body2">
        {lang.scheduleComponent.preview.promosAndProducts}
      </ProductOverrideRightSideBarHeader>
      <ProductOverrideRightSideBarContentWrapper
        $hasSelectedProduct={localAttributes.length > 0}>
        {isLoading && localAttributes.length === 0 ? (
          <InitialLoadingWrapper>
            <Progress />
          </InitialLoadingWrapper>
        ) : localAttributes.length === 0 ? (
          <EmptyStateWrapper>
            <EmptyStateMessage variant="body2">
              {lang.scheduleComponent.preview.selectProductToViewAttributes}
            </EmptyStateMessage>
          </EmptyStateWrapper>
        ) : (
          <ProductAttributeAccordion
            sizes={localAttributes}
            defaultSize={defaultSize}
            defaultFlavor={defaultFlavor}
            showImage={showImage}
            defaultImage={defaultImage}
            productVariationsAssets={productVariationsAssets}
            onChange={handleChange}
            onDefaultFlavorChange={handleDefaultFlavorChange}
            onDefaultSizeChange={handleDefaultSizeChange}
            onShowImageChange={handleShowImageChange}
            onProductImageChange={handleProductImageChange}
          />
        )}
      </ProductOverrideRightSideBarContentWrapper>
    </ProductOverrideRightSideBarWrapper>
  );
};
